import { Selectable } from '../../utils/CommonTypes/CommonTypes';

export type School = {
    id: number;
    name: string;
};

export type Tag = {
    Id: number;
    FairId: number;
    Type: 'person' | 'project' | 'auto';
    Value: string;
};

export type CombinedTagOption = Selectable & {
    tagType: 'person' | 'project' | 'auto';
};

export interface IBulkEmailComposeState {
    RecipientGroups?: string[];
    SchoolFilter?: number[];
    EntryStatusFilter?: string[];
    Tags?: number[];
    ExcludeTags?: number[];
    RequireAllTags: boolean;
    CategoryFilter?: number[];
    GradeFilter?: number[];
    DivisionFilter?: number[];
}

export interface IAttachmentLink {
    FileId: string;
    FileName: string;
    ContentType: string;
    SizeBytes: number;
    Url: string;
}

export interface IBulkEmailHistoryItem {
    PublicId: string;
    CreatedAtUtc: string;
    SentAtUtc?: string;
    SentBy: string;
    Subject: string;
    Message: string;
    RecipientCount: number;
    SentCount: number;
    ErrorCount: number;
    GroupMessageId?: string;
    ComposeState?: IBulkEmailComposeState;
    Attachments?: IAttachmentLink[];
}

export interface IBulkMessageResults {
    IsPreview: boolean;
    ErrorMessages: string[];
    ToList: IMessageResult[];
    StartedAtUtc: string;
    FinishedAtUtc: string;
    Finished: boolean;
    TriedCount: number;
    SentCount: number;
    ErrorsCount: number;
    EmailsCount: number;
    SmsCount: number;
}

export interface IMessageResult {
    Email: string;
    AllowTextMessage: boolean;
    Phone: string;
    SentAtUtc: string;
    Subject: string;
    Body: string;
}

export interface IBulkEmailRecipientDetail {
    MessageSentId: number;
    PublicId?: string;
    Email: string;
    PhoneNumber: string;
    ErrorMessage: string;
    FirstName: string;
    LastName: string;
}

export interface IBulkEmailDeliveryRecipient {
    MessageId?: string;
    Email?: string;
    WasSent: boolean;
    WasRead: boolean;
    LastReadAtUtc?: string;
    LatestAction?: string;
}

export interface IBulkEmailDeliveryStatus {
    Available: boolean;
    GroupMessageId?: string;
    Capped: boolean;
    TrackedCount: number;
    SentCount: number;
    ReadCount: number;
    ErrorCount: number;
    Recipients?: IBulkEmailDeliveryRecipient[];
}

export interface IScheduleRelationship {
    SystemDateKey: string;
    DayOffset: number;
}

export type BulkMessageStatus = 'Pending' | 'Processing' | 'Finished' | 'Canceled';

export interface IScheduledBulkMessage {
    PublicId: string;
    Status: BulkMessageStatus;
    IsScheduled: boolean;
    ScheduledAtUtc?: string;
    ScheduleRelationship?: IScheduleRelationship;
    ResolvedPreviewAtUtc?: string;
    CreatedAtUtc: string;
    SentBy: string;
    Subject: string;
    Message: string;
    ComposeState?: IBulkEmailComposeState;
    Attachments?: IAttachmentLink[];
}

// Keep in sync with SystemDateResolver.IsValidSystemDateKey/Resolve on the backend.
export const SYSTEM_DATE_OPTIONS: Selectable[] = [
    { value: 'registration_start.fair', label: 'Participant Registration Start' },
    { value: 'registration_end.fair', label: 'Participant Registration End' },
    { value: 'late_registration_end.fair', label: 'Participant Registration Late End' },
    { value: 'post_awards.fair', label: 'Post Awards' },
    { value: 'fair_start.fair', label: 'Fair Start' },
    { value: 'fair_end.fair', label: 'Fair End' },
    { value: 'judging_start.fair', label: 'Judging Start' },
    { value: 'judging_end.fair', label: 'Judging End' },
];

export const recipientGroupLabels = (composeState: IBulkEmailComposeState | undefined, recipientCategories: Selectable[]) => {
    const groups = composeState?.RecipientGroups ?? [];
    if (groups.length === 0) return 'Unknown';
    return groups
        .map((value) => recipientCategories.find((x) => x.value === value)?.label ?? value)
        .join(', ');
};

export const schoolFilterLabels = (composeState: IBulkEmailComposeState | undefined, schoolOptions: Selectable[]) => {
    return (composeState?.SchoolFilter ?? [])
        .map((value) => schoolOptions.find((x) => x.value === value)?.label ?? value)
        .join(', ');
};

export const idFilterLabels = (ids: number[] | undefined, options: Selectable[]) => {
    return (ids ?? [])
        .map((value) => options.find((x) => String(x.value) === String(value))?.label ?? value)
        .join(', ');
};

export const entryStatusFilterLabels = (composeState: IBulkEmailComposeState | undefined, entryStatusOptions: Selectable[]) => {
    return (composeState?.EntryStatusFilter ?? [])
        .map((value) => entryStatusOptions.find((x) => x.value === value)?.label ?? value)
        .join(', ');
};

export const tagLabels = (ids: number[] | undefined, tagOptions: CombinedTagOption[]) => {
    return (ids ?? [])
        .map((value) => tagOptions.find((x) => x.value === value)?.label ?? value)
        .join(', ');
};

export const hasTagFilters = (composeState: IBulkEmailComposeState | undefined) => {
    return (composeState?.Tags?.length ?? 0) > 0 || (composeState?.ExcludeTags?.length ?? 0) > 0;
};
